import { cn } from "@tellescope/utils";
import { TellescopeFormFieldBaseProps } from "../../types";
import { Checkbox, Input, Label } from "@tellescope/ui";
import { Trans, useTranslation } from "react-i18next";
import { FieldDescription } from "../../FieldDescription";
import { SignatureDocument } from "./SignatureDocument";
import { useSignatureFieldLogic } from "./useSignatureFieldLogic";

export interface SignatureFieldProps extends TellescopeFormFieldBaseProps<"signature"> {
  fullName: string;
}

export const SignatureField = (props: SignatureFieldProps) => {
  const { id, title, description, htmlDescription, className, options, error, required } = props;
  const { t } = useTranslation("forms");
  const { consent, signature, prefillName, handleConsentChange, handleSignatureChange, handleBlur } =
    useSignatureFieldLogic(props);

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      {!!(options?.pdfAttachment || options?.signatureUrl) && (
        <div className="pb-2">
          <SignatureDocument pdfAttachment={options.pdfAttachment} signatureUrl={options.signatureUrl} />
        </div>
      )}
      <div className="flex gap-2 items-center">
        <Checkbox
          id={`${id}-consent`}
          checked={consent}
          onCheckedChange={handleConsentChange}
          aria-label={t("consent to e-signature checkbox")}
        />
        <Label htmlFor={`${id}-consent`} className="gap-1">
          <Trans ns="forms">
            I consent to use{" "}
            <a
              className="underline"
              href={`/e-signature-terms?name=${encodeURIComponent(options?.esignatureTermsCompanyName ?? "")}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              electronic signatures
            </a>
          </Trans>
        </Label>
      </div>
      <div className="flex flex-col gap-1">
        <Input
          value={signature}
          onChange={handleSignatureChange}
          placeholder={prefillName || t("Full Name")}
          disabled={!consent}
          onBlur={handleBlur}
          className={cn(!!error && "border-destructive")}
          aria-invalid={!!error}
        />
        <p className={`text-muted-foreground text-sm ${consent ? "" : "opacity-50"}`}>
          {t("Enter your legal full name to complete the signature")}
        </p>
      </div>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
